/**
 * Social Zone Service
 * ===================
 * 
 * Frontend service for social zone interactions (seating, presence).
 * 
 * Social zones are physical areas where players gather for conversations.
 * This service handles:
 * - Fetching zone data for a scene
 * - Sitting/standing interactions
 * - Zone presence tracking
 * - Voice/chat-ready event hooks
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get all social zones in a scene
 * @param {string} sceneId - Scene ID
 * @returns {Promise<{sceneId: string, zones: Array}>}
 */
export async function getSceneZones(sceneId) {
  const token = localStorage.getItem('midgard_token');
  const response = await fetch(`${API_URL}/api/social-zones/${sceneId}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch zones');
  }
  
  return response.json();
}

/**
 * Get occupancy counts for zones in a scene
 * @param {string} sceneId - Scene ID
 * @returns {Promise<{sceneId: string, zones: Object}>}
 */
export async function getZonePresence(sceneId) {
  const response = await fetch(`${API_URL}/api/social-zones/${sceneId}/presence`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch zone presence');
  }
  
  return response.json();
}

/**
 * Sit at a social zone seat
 * @param {string} sceneId - Scene ID
 * @param {string} zoneId - Zone ID
 * @param {string|null} seatId - Specific seat ID (optional)
 * @returns {Promise<{success: boolean, seat: Object, zone: Object}>}
 */
export async function sitAtZone(sceneId, zoneId, seatId = null) {
  const token = localStorage.getItem('midgard_token');
  if (!token) {
    throw new Error('Authentication required');
  }
  
  const response = await fetch(`${API_URL}/api/social-zones/${sceneId}/sit`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      zone_id: zoneId,
      seat_id: seatId,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to sit' }));
    throw new Error(error.detail || 'Failed to sit');
  }
  
  return response.json();
}

/**
 * Stand up from current seat
 * @returns {Promise<{success: boolean, wasSeated: boolean, previousZone: Object|null}>}
 */
export async function standUp() {
  const token = localStorage.getItem('midgard_token');
  if (!token) {
    throw new Error('Authentication required');
  }
  
  const response = await fetch(`${API_URL}/api/social-zones/stand`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to stand');
  }
  
  return response.json();
}

/**
 * Get current user's seat info
 * @returns {Promise<{seated: boolean, seat: Object|null}>}
 */
export async function getMySeat() {
  const token = localStorage.getItem('midgard_token');
  if (!token) {
    return { seated: false, seat: null };
  }
  
  const response = await fetch(`${API_URL}/api/social-zones/my-seat`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    return { seated: false, seat: null };
  }
  
  return response.json();
}

// ================================
// VOICE/CHAT-READY EVENT SYSTEM
// ================================

// Event callbacks - to be connected to voice/chat systems later
const socialZoneCallbacks = {
  onJoinZone: [],   // (zoneId, zoneData) => {}
  onLeaveZone: [],  // (zoneId) => {}
  onZoneOccupancyChange: [], // (zoneId, occupants) => {}
};

/**
 * Register a callback for zone join events
 * @param {Function} callback - (zoneId, zoneData) => {}
 */
export function onJoinSocialZone(callback) {
  socialZoneCallbacks.onJoinZone.push(callback);
  return () => {
    const idx = socialZoneCallbacks.onJoinZone.indexOf(callback);
    if (idx >= 0) socialZoneCallbacks.onJoinZone.splice(idx, 1);
  };
}

/**
 * Register a callback for zone leave events
 * @param {Function} callback - (zoneId) => {}
 */
export function onLeaveSocialZone(callback) {
  socialZoneCallbacks.onLeaveZone.push(callback);
  return () => {
    const idx = socialZoneCallbacks.onLeaveZone.indexOf(callback);
    if (idx >= 0) socialZoneCallbacks.onLeaveZone.splice(idx, 1);
  };
}

/**
 * Emit zone join event (called internally when sit succeeds)
 */
export function emitJoinZone(zoneId, zoneData) {
  console.log('SocialZone: Joined', zoneId);
  socialZoneCallbacks.onJoinZone.forEach(cb => {
    try { cb(zoneId, zoneData); } catch (e) { console.error(e); }
  });
}

/**
 * Emit zone leave event (called internally when stand succeeds)
 */
export function emitLeaveZone(zoneId) {
  console.log('SocialZone: Left', zoneId);
  socialZoneCallbacks.onLeaveZone.forEach(cb => {
    try { cb(zoneId); } catch (e) { console.error(e); }
  });
}

// ================================
// ZONE TYPE UTILITIES
// ================================

export const ZONE_TYPE_LABELS = {
  bar: 'Bar',
  table: 'Table',
  hearth: 'Hearth',
  bench: 'Bench',
  couch: 'Couch',
  campfire: 'Campfire',
  meeting: 'Meeting Room',
  general: 'Gathering Area',
};

export const getZoneTypeLabel = (type) => ZONE_TYPE_LABELS[type] || 'Area';

/**
 * Format zone presence text
 * @param {number} count - Number of people
 * @param {number} capacity - Max capacity
 * @returns {string}
 */
export function formatZonePresence(count, capacity = null) {
  if (count === 0) return 'Empty';
  if (count === 1) return '1 person';
  if (capacity) return `${count}/${capacity} people`;
  return `${count} people`;
}

export default {
  getSceneZones,
  getZonePresence,
  sitAtZone,
  standUp,
  getMySeat,
  onJoinSocialZone,
  onLeaveSocialZone,
  emitJoinZone,
  emitLeaveZone,
  formatZonePresence,
  getZoneTypeLabel,
};
